import { Link } from "react-router-dom";
import {
  ArrowRight,
  Microscope,
  TrendingUp,
  AlertTriangle,
  XCircle,
  BarChart3,
  ScrollText,
  Activity,
  Target,
  Lock,
} from "lucide-react";

const FEATURES = [
  {
    icon: Microscope,
    label: "01 · 1,000-GAME SIM",
    title: "Monte Carlo every game",
    body: "Claude Sonnet 4.5 reasons through pitcher, weather, injury report and line — then projects a 1,000-game distribution per side.",
    visual: (
      <div className="mt-4 mono text-[10px]">
        <div className="text-zinc-400 mb-1.5">Yankees vs Red Sox · 1,000 sims</div>
        <div className="flex h-2 border border-zinc-800">
          <div className="bg-[#00D4FF]/40" style={{ width: "47%" }} />
          <div className="bg-[#FFBF00]/40" style={{ width: "53%" }} />
        </div>
        <div className="flex justify-between text-zinc-500 mt-1">
          <span>BOS 47%</span><span>NYY 53%</span>
        </div>
      </div>
    ),
  },
  {
    icon: TrendingUp,
    label: "02 · BEST EDGE",
    title: "Ranked +EV plays",
    body: "Every game on the slate scored against the live DraftKings price. Plays sorted by edge, filtered to viable progression candidates.",
    visual: (
      <div className="mt-4 mono text-[10px] space-y-1">
        <div className="flex justify-between text-zinc-400 border-b border-zinc-800 pb-1">
          <span>VGK ML</span><span className="text-[#00D4FF]">+5.6%</span>
        </div>
        <div className="flex justify-between text-zinc-400 border-b border-zinc-800 pb-1">
          <span>NYY -1.5</span><span className="text-[#00D4FF]">+4.2%</span>
        </div>
        <div className="flex justify-between text-zinc-400">
          <span>UFC · Pereira ML</span><span className="text-[#00D4FF]">+3.1%</span>
        </div>
      </div>
    ),
  },
  {
    icon: AlertTriangle,
    label: "03 · TRAP DETECTOR",
    title: "Fade the public",
    body: "Public-side ≥ 60% with sharp money the other way? Trap. Score 0-100 surfaces the games to fade.",
    visual: (
      <div className="mt-4 mono text-[10px]">
        <div className="text-zinc-400 mb-1.5">LAL vs BOS · Public on LAL</div>
        <div className="flex items-baseline justify-between mb-1">
          <span className="amber-text">TRAP 78</span>
          <span className="text-zinc-500">/100</span>
        </div>
        <div className="h-1 bg-zinc-900 border border-zinc-800">
          <div className="h-full bg-[#FFBF00]/70" style={{ width: "78%" }} />
        </div>
      </div>
    ),
  },
  {
    icon: XCircle,
    label: "04 · NO-BET",
    title: "Discipline first",
    body: "AI explicitly flags games to skip — unknown starter, weather wipeout risk, championship-fight upset profile, low data quality.",
    visual: (
      <div className="mt-4 mono text-[10px] space-y-1.5 text-zinc-400">
        <div className="flex gap-2"><span className="text-red-400">!</span><span>Starter unknown</span></div>
        <div className="flex gap-2"><span className="text-red-400">!</span><span>Heavy fav (-280)</span></div>
        <div className="flex gap-2"><span className="text-red-400">!</span><span>Wind 22 mph</span></div>
      </div>
    ),
  },
  {
    icon: BarChart3,
    label: "05 · DISAGREEMENT",
    title: "Market mispricing radar",
    body: "Absolute gap between fair line and DraftKings, in basis points. 100 bps = 1% mispricing.",
    visual: (
      <div className="mt-4 mono text-[10px]">
        <div className="text-zinc-400 mb-1.5">Fair −138 · DK −166</div>
        <div className="flex items-baseline justify-between mb-1">
          <span className="text-zinc-300">Δ 480 bps</span>
          <span className="text-[#00D4FF]">+EV</span>
        </div>
        <div className="h-1 bg-zinc-900 border border-zinc-800">
          <div className="h-full bg-[#FFBF00]/70" style={{ width: "32%" }} />
        </div>
      </div>
    ),
  },
  {
    icon: Activity,
    label: "06 · SIM CONFIDENCE",
    title: "Variance, surfaced",
    body: "Every sim returns a low/high confidence band. Tells you which plays are knife-edge vs. genuinely lopsided.",
    visual: (
      <div className="mt-4 mono text-[10px]">
        <div className="text-zinc-400 mb-1.5">Model 53% · band 49–58%</div>
        <div className="relative h-2 bg-zinc-900 border border-zinc-800">
          <div
            className="absolute top-0 h-full bg-[#00D4FF]/40"
            style={{ left: "49%", width: "9%" }}
          />
          <div
            className="absolute top-[-2px] h-[10px] w-[2px] bg-[#00D4FF]"
            style={{ left: "53%" }}
          />
        </div>
      </div>
    ),
  },
  {
    icon: Target,
    label: "07 · DATA QUALITY",
    title: "Score every signal",
    body: "0–100 score for how much real context we had. Live odds, lineup, injuries — all tracked. We tell you when the model is guessing.",
    visual: (
      <div className="mt-4 mono text-[10px]">
        <div className="text-zinc-400 mb-1.5">All signals present</div>
        <div className="flex items-baseline justify-between mb-1">
          <span className="text-[#00D4FF]">DQ 92</span>
          <span className="text-zinc-500">/100</span>
        </div>
        <div className="h-1 bg-zinc-900 border border-zinc-800">
          <div className="h-full bg-[#00D4FF]/70" style={{ width: "92%" }} />
        </div>
      </div>
    ),
  },
  {
    icon: ScrollText,
    label: "08 · PUBLIC LEDGER",
    title: "We grade every pick",
    body: "Every AI play we make goes into a public ledger. Result, final score, and Closing Line Value graded after the close.",
    visual: (
      <div className="mt-4 mono text-[10px] space-y-1.5">
        <div className="flex items-center justify-between text-zinc-400">
          <span>VGK ML</span>
          <span className="px-1.5 border border-[#00D4FF] text-[#00D4FF]">WIN</span>
          <span className="text-[#00D4FF]">CLV +3.2%</span>
        </div>
        <div className="flex items-center justify-between text-zinc-400">
          <span>NYY ML</span>
          <span className="px-1.5 border border-red-500 text-red-400">LOSS</span>
          <span className="text-[#00D4FF]">CLV +1.1%</span>
        </div>
      </div>
    ),
  },
];

function FeatureCard({ f }) {
  const Icon = f.icon;
  return (
    <div
      className="border border-zinc-800 p-5 hover:border-[#00D4FF]/40 transition-colors"
      data-testid={`what-we-do-card-${f.label.split(" · ")[0]}`}
    >
      <div className="flex items-start justify-between mb-3">
        <Icon size={18} className="text-[#00D4FF]" strokeWidth={1.5} />
        <div className="overline">{f.label}</div>
      </div>
      <div className="font-display text-xl leading-tight mb-2">{f.title}</div>
      <p className="text-sm text-zinc-400 leading-relaxed">{f.body}</p>
      {f.visual}
    </div>
  );
}

export default function WhatWeDoSection() {
  return (
    <section
      className="relative z-10 border-y border-zinc-800 py-24 bg-zinc-950/40"
      data-testid="what-we-do-section"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <div className="grid lg:grid-cols-12 gap-10 mb-12 items-end">
          <div className="lg:col-span-7">
            <div className="overline neon-text mb-4 flex items-center gap-2">
              <span className="text-[#00D4FF]">●</span>
              WHAT YOU GET
            </div>
            <h2 className="font-display text-4xl lg:text-6xl leading-[1.05] tracking-tighter">
              Eight tools.{" "}
              <span className="text-[#00D4FF]">One terminal.</span>
            </h2>
          </div>
          <div className="lg:col-span-5">
            <p className="text-zinc-400 leading-relaxed">
              Every game on tonight's slate evaluated by the AI, scored by the
              market, and graded after the close. Not picks. <span className="text-zinc-200">Process.</span>
            </p>
          </div>
        </div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4" data-testid="what-we-do-grid">
          {FEATURES.map((f) => <FeatureCard key={f.label} f={f} />)}
        </div>

        {/* Buy-in band */}
        <div
          className="mt-12 border border-[#00D4FF]/40 bg-[#00D4FF]/5 p-8 lg:p-10 grid lg:grid-cols-12 gap-6 items-center"
          data-testid="what-we-do-buyin"
        >
          <div className="lg:col-span-7">
            <div className="overline neon-text mb-2">UPGRADE · BUY IN</div>
            <h3 className="font-display text-3xl lg:text-4xl leading-tight mb-2">
              Three days free.{" "}
              <span className="text-[#00D4FF]">$9.99/week after.</span>
            </h3>
            <p className="text-sm text-zinc-300 max-w-xl leading-relaxed">
              Unlock the full slate, every panel, every Claude write-up, every
              CLV-graded pick. Cancel any time from your billing page — no
              card-not-charged tricks.
            </p>
          </div>
          <div className="lg:col-span-5 flex flex-col sm:flex-row gap-3 lg:justify-end">
            <Link
              to="/register"
              data-testid="what-we-do-cta-primary"
              className="inline-flex items-center justify-center gap-2 px-6 py-4 bg-[#00D4FF] text-black mono uppercase tracking-widest text-xs font-medium hover:brightness-110 shadow-[0_0_24px_rgba(0,212,255,0.3)]"
            >
              <Lock size={12} strokeWidth={2} />
              Start 3 free days
              <ArrowRight size={14} strokeWidth={1.75} />
            </Link>
            <Link
              to="/ledger"
              data-testid="what-we-do-cta-secondary"
              className="inline-flex items-center justify-center gap-2 px-6 py-4 border border-zinc-700 mono uppercase tracking-widest text-xs hover:border-[#00D4FF] hover:text-[#00D4FF]"
            >
              See proof first →
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
}
